// AI Service for multiple providers with streaming support
class AIService {
  constructor() {
    this.providers = {
      openai: {
        name: 'OpenAI',
        endpoint: 'https://api.openai.com/v1/chat/completions',
        model: 'gpt-3.5-turbo'
      },
      groq: {
        name: 'Groq',
        endpoint: 'https://api.groq.com/openai/v1/chat/completions',
        model: 'llama3-8b-8192'
      },
      claude: {
        name: 'Claude',
        endpoint: 'https://api.anthropic.com/v1/messages',
        model: 'claude-3-sonnet-20240229'
      },
      gemini: {
        name: 'Gemini',
        endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent',
        model: 'gemini-pro'
      }
    };
  }

  // Get the first enabled AI provider from system settings
  getEnabledProvider(systemSettings) {
    if (!systemSettings?.aiModels) return null;

    const models = systemSettings.aiModels;
    
    // Check in priority order: OpenAI, Groq, Claude, Gemini, then Ollama
    if (models.openai?.enabled && models.openai?.apiKey) {
      return { type: 'openai', config: models.openai };
    }
    if (models.groq?.enabled && models.groq?.apiKey) {
      return { type: 'groq', config: models.groq };
    }
    if (models.claude?.enabled && models.claude?.apiKey) {
      return { type: 'claude', config: models.claude };
    }
    if (models.gemini?.enabled && models.gemini?.apiKey) {
      return { type: 'gemini', config: models.gemini };
    }
    if (models.ollama?.enabled) {
      return { type: 'ollama', config: models.ollama };
    }

    return null;
  }

  // Generate non-streaming response
  async generateResponse(message, context, systemSettings, provider, personalization = null) {
    console.log('🔍 AI Service: Generating non-streaming response...');
    console.log('📋 Provider:', provider);
    console.log('👤 Personalization:', personalization);
    
    if (!provider) {
      console.log('❌ No AI provider provided - throwing error');
      throw new Error('No AI provider provided');
    }

    console.log(`🚀 Using ${provider.type} provider for response generation`);

    // Add systemSettings and personalization to provider config for access in individual methods
    const configWithSettings = { ...provider.config, systemSettings, personalization };

    switch (provider.type) {
      case 'openai':
        return await this.generateOpenAINonStreamingResponse(message, context, configWithSettings);
      case 'groq':
        return await this.generateGroqNonStreamingResponse(message, context, configWithSettings);
      case 'claude':
        return await this.generateClaudeNonStreamingResponse(message, context, configWithSettings);
      case 'gemini':
        return await this.generateGeminiNonStreamingResponse(message, context, configWithSettings);
      case 'ollama':
        console.log('🦙 Using Ollama for response generation');
        return await this.generateOllamaNonStreamingResponse(message, context, configWithSettings);
      default:
        throw new Error(`Unsupported provider: ${provider.type}`);
    }
  }

  // Generate streaming response
  async generateStreamingResponse(message, context, language, systemSettings, onChunk) {
    console.log('🔍 AI Service: Checking for enabled providers...');
    console.log('📋 System settings received:', systemSettings?.aiModels);
    
    const provider = this.getEnabledProvider(systemSettings);
    console.log('🎯 Provider detected:', provider);
    
    if (!provider) {
      console.log('❌ No AI provider enabled - throwing error');
      throw new Error('No AI provider enabled');
    }

    console.log(`🚀 Using ${provider.type} provider for response generation`);

    // Add systemSettings to provider config for access in individual methods
    const configWithSettings = { ...provider.config, systemSettings };

    switch (provider.type) {
      case 'openai':
        return await this.generateOpenAIResponse(message, context, language, configWithSettings, onChunk);
      case 'groq':
        return await this.generateGroqResponse(message, context, language, configWithSettings, onChunk);
      case 'claude':
        return await this.generateClaudeResponse(message, context, language, configWithSettings, onChunk);
      case 'gemini':
        return await this.generateGeminiResponse(message, context, language, configWithSettings, onChunk);
      case 'ollama':
        console.log('🦙 Using Ollama for response generation');
        return await this.generateOllamaResponse(message, context, language, configWithSettings, onChunk);
      default:
        throw new Error(`Unsupported provider: ${provider.type}`);
    }
  }

  // OpenAI/Groq compatible streaming
  async generateOpenAIResponse(message, context, language, config, onChunk) {
    const systemPrompt = this.buildSystemPrompt(context, language, config.systemSettings, config.personalization);
    
    const response = await fetch(this.providers.openai.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.apiKey}`
      },
      body: JSON.stringify({
        model: this.providers.openai.model,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: message }
        ],
        stream: true,
        temperature: 0.7,
        max_tokens: 1000
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return await this.processStreamingResponse(response, onChunk);
  }

  // Groq streaming
  async generateGroqResponse(message, context, language, config, onChunk) {
    const systemPrompt = this.buildSystemPrompt(context, language, config.systemSettings, config.personalization);
    
    const response = await fetch(this.providers.groq.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.apiKey}`
      },
      body: JSON.stringify({
        model: this.providers.groq.model,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: message }
        ],
        stream: true,
        temperature: 0.7,
        max_tokens: 1000
      })
    });

    if (!response.ok) {
      throw new Error(`Groq API request failed: ${response.statusText}`);
    }

    return await this.processStreamingResponse(response, onChunk);
  }

  // Claude streaming
  async generateClaudeResponse(message, context, language, config, onChunk) {
    const systemPrompt = this.buildSystemPrompt(context, language, config.systemSettings, config.personalization);
    
    const response = await fetch(this.providers.claude.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey,
        'anthropic-version': '2023-06-01'
      },
      body: JSON.stringify({
        model: this.providers.claude.model,
        max_tokens: 1000,
        system: systemPrompt,
        messages: [
          { role: 'user', content: message }
        ],
        stream: true
      })
    });

    if (!response.ok) {
      throw new Error(`Claude API request failed: ${response.statusText}`);
    }

    return await this.processClaudeStream(response, onChunk);
  }

  // Gemini streaming
  async generateGeminiResponse(message, context, language, config, onChunk) {
    const systemPrompt = this.buildSystemPrompt(context, language, config.systemSettings, config.personalization);
    const fullPrompt = `${systemPrompt}\n\nUser: ${message}`;
    
    const response = await fetch(`${this.providers.gemini.endpoint}?key=${config.apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contents: [{
          parts: [{ text: fullPrompt }]
        }],
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 1000
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Gemini API request failed: ${response.statusText}`);
    }

    const data = await response.json();
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response generated';
    
    // Simulate streaming for Gemini (it doesn't support streaming yet)
    return await this.simulateStreaming(text, onChunk);
  }

  // Ollama streaming (existing functionality)
  async generateOllamaResponse(message, context, language, config, onChunk) {
    const ollamaService = await import('./ollamaService');
    return await ollamaService.default.generateStreamingResponse(message, context, language, onChunk, config.systemSettings);
  }

  // Non-streaming methods
  async generateOpenAINonStreamingResponse(message, context, config) {
    const systemPrompt = this.buildSystemPrompt(context, 'en', config.systemSettings, config.personalization);
    
    const response = await fetch(this.providers.openai.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.apiKey}`
      },
      body: JSON.stringify({
        model: this.providers.openai.model,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: message }
        ],
        stream: false,
        temperature: 0.7,
        max_tokens: 1000
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.choices?.[0]?.message?.content || 'No response generated';
  }

  async generateGroqNonStreamingResponse(message, context, config) {
    const systemPrompt = this.buildSystemPrompt(context, 'en', config.systemSettings, config.personalization);
    
    const response = await fetch(this.providers.groq.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.apiKey}`
      },
      body: JSON.stringify({
        model: this.providers.groq.model,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: message }
        ],
        stream: false,
        temperature: 0.7,
        max_tokens: 1000
      })
    });

    if (!response.ok) {
      throw new Error(`Groq API request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.choices?.[0]?.message?.content || 'No response generated';
  }

  async generateClaudeNonStreamingResponse(message, context, config) {
    const systemPrompt = this.buildSystemPrompt(context, 'en', config.systemSettings, config.personalization);
    
    const response = await fetch(this.providers.claude.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey,
        'anthropic-version': '2023-06-01'
      },
      body: JSON.stringify({
        model: this.providers.claude.model,
        max_tokens: 1000,
        system: systemPrompt,
        messages: [
          { role: 'user', content: message }
        ]
      })
    });

    if (!response.ok) {
      throw new Error(`Claude API request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.content?.[0]?.text || 'No response generated';
  }

  async generateGeminiNonStreamingResponse(message, context, config) {
    const systemPrompt = this.buildSystemPrompt(context, 'en', config.systemSettings, config.personalization);
    const fullPrompt = `${systemPrompt}\n\nUser: ${message}`;
    
    const response = await fetch(`${this.providers.gemini.endpoint}?key=${config.apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contents: [{
          parts: [{ text: fullPrompt }]
        }],
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 1000
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Gemini API request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response generated';
  }

  async generateOllamaNonStreamingResponse(message, context, config) {
    const ollamaService = await import('./ollamaService');
    return await ollamaService.default.generateResponse(message, context, 'en', config.systemSettings);
  }

  // Process streaming response for OpenAI/Groq
  async processStreamingResponse(response, onChunk) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content;
              if (content) {
                fullResponse += content;
                onChunk(content);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return fullResponse;
  }

  // Process Claude streaming
  async processClaudeStream(response, onChunk) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              if (parsed.type === 'content_block_delta') {
                const content = parsed.delta?.text;
                if (content) {
                  fullResponse += content;
                  onChunk(content);
                }
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return fullResponse;
  }

  // Simulate streaming for APIs that don't support it
  async simulateStreaming(text, onChunk) {
    const words = text.split(' ');
    let currentText = '';

    for (let i = 0; i < words.length; i++) {
      const word = words[i] + (i < words.length - 1 ? ' ' : '');
      currentText += word;
      onChunk(word);
      
      // Add small delay to simulate streaming
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    return text;
  }

  // Build system prompt with context
  buildSystemPrompt(context, language, systemSettings, personalization = null) {
    // Get AI assistant settings
    const aiAssistant = systemSettings?.aiAssistant || {
      name: 'Jeje',
      description: 'Senior AI Advisory Consultant at Scalovate',
      personality: 'Mature, professional advisory consultant specializing in AI adoption and business transformation',
      communicationGuidelines: 'Maintain a mature, consultative approach. Speak as an experienced business advisor.',
      responseMaturity: 'generic'
    };

    let prompt = `You are ${aiAssistant.name}, a ${aiAssistant.description}. 

PERSONALITY & BEHAVIOR:
${aiAssistant.personality}

COMMUNICATION STYLE:
${aiAssistant.communicationGuidelines || 'Maintain a mature, consultative approach. Speak as an experienced business advisor. Use professional language without being overly formal. Demonstrate deep understanding of business challenges and provide thoughtful, strategic insights.'}

RESPONSE LANGUAGE: ${language === 'ar' ? 'Arabic' : 'English'}

${personalization?.isFirstMessage && personalization?.userName ? 
`PERSONALIZATION: This is the user's first message and their name is ${personalization.userName}. Start your response with "Hello ${personalization.userName}!" to greet them personally. Only use their name in this first response - do not use their name in subsequent responses.` : 
'PERSONALIZATION: Respond naturally and professionally. If this is a first interaction, provide a warm welcome without using specific names.'}

`;

    // Add seniority-based response guidelines if enabled
    if (aiAssistant.responseMaturity === 'seniority-based' && personalization?.userDesignation) {
      const designation = personalization.userDesignation.toLowerCase();
      
      if (['intern', 'junior'].includes(designation)) {
        prompt += `SENIORITY-BASED RESPONSE GUIDELINES:
- User is at ${designation} level - provide foundational explanations
- Use simple, clear language and avoid complex jargon
- Include basic concepts and step-by-step guidance
- Provide educational context for technical terms
- Focus on learning opportunities and skill development
- Encourage questions and provide supportive guidance

`;
      } else if (['mid-level', 'senior'].includes(designation)) {
        prompt += `SENIORITY-BASED RESPONSE GUIDELINES:
- User is at ${designation} level - provide balanced technical depth
- Use professional terminology with brief explanations when needed
- Focus on practical implementation and best practices
- Include strategic considerations and trade-offs
- Provide actionable insights and recommendations
- Balance technical details with business impact

`;
      } else if (['lead', 'manager', 'director'].includes(designation)) {
        prompt += `SENIORITY-BASED RESPONSE GUIDELINES:
- User is at ${designation} level - focus on strategic and leadership aspects
- Use advanced terminology and assume technical familiarity
- Emphasize business impact, ROI, and strategic implications
- Include team management and organizational considerations
- Focus on decision-making frameworks and risk assessment
- Provide high-level insights with implementation roadmaps

`;
      } else if (['vp', 'ceo', 'cto'].includes(designation)) {
        prompt += `SENIORITY-BASED RESPONSE GUIDELINES:
- User is at executive ${designation} level - provide executive-level insights
- Focus on strategic vision, competitive advantage, and market positioning
- Emphasize organizational transformation and change management
- Include industry trends, market analysis, and future outlook
- Provide concise, high-impact recommendations
- Focus on scalability, enterprise-wide implications, and long-term strategy

`;
      }
    }

    prompt += `CONVERSATION GUIDELINES:
- Keep responses concise (2-3 lines maximum)
- Always end with a relevant follow-up question
- Focus on understanding business needs and challenges
- Guide discussions toward practical AI adoption strategies
- Handle both technical and non-technical conversations professionally
- Answer generic questions while steering toward business applications

`;

    if (context && context.length > 0) {
      // Found relevant content in database
      const item = context[0];
      prompt += `🎯 RELEVANT COMPANY INFORMATION:
Topic: ${item.subject}
Details: ${item.details}
Keywords: ${item.keywords}

NATURAL RESPONSE APPROACH:
- Respond naturally as if you have this knowledge from experience
- Don't mention "database" or "knowledge base" - speak as if it's your expertise
- Connect the information to their business needs naturally
- Ask strategic follow-up questions about their specific situation
- Example: "That's an excellent question. In my experience working with organizations on ${item.subject}, I've seen this typically help businesses achieve [benefit]. What specific challenges are you facing in this area?"

`;
    } else {
      // Not in database - natural conversation steering
      prompt += `💼 NATURAL CONVERSATION GUIDANCE:

HUMAN-LIKE RESPONSE APPROACH:
- Respond positively and naturally to their question
- Don't mention that information isn't available - just pivot naturally
- Show genuine interest in their topic
- Smoothly guide conversation toward business applications
- Ask engaging questions about their situation
- Example: "That's a fascinating area! I work with many organizations exploring similar concepts. At Scalovate, we often help companies leverage AI to address these types of challenges. What industry are you working in?"

SCALOVATE SOLUTIONS TO NATURALLY REFERENCE:
- AI Infrastructure & Implementation
- Custom AI Application Development  
- AI Strategy & Adoption Consulting
- Large Language Model Integration
- Private AI Solutions & Security
- Intelligent Automation Systems
- AI Training & Change Management

`;
    }

    prompt += `
LEAD QUALIFICATION APPROACH:
After 4 meaningful exchanges, naturally suggest connecting with human specialists:
"I can see you have some specific requirements that would benefit from a more detailed discussion. Would it be helpful if I connected you with one of my human colleagues who can better understand your unique situation and explain our solutions in detail? They can provide personalized recommendations based on your specific needs."

MAINTAIN PROFESSIONAL TONE:
- Be knowledgeable and confident
- Show genuine interest in their business success
- Provide value in every interaction
- Build trust through expertise and insight
- Keep conversations focused and productive`;
    
    return prompt;
  }
}

export default new AIService(); 